import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, CircularProgress, FormControl, InputLabel,
    Select, MenuItem, TableSortLabel, Avatar, Grid, Checkbox, ListItemText, FormControlLabel
} from '@mui/material';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { seasonPlayersCollection } from '../../services/players';
import { db, functions } from '../../firebase';
import { useAuction } from '../../context/AuctionContext';
import { httpsCallable } from 'firebase/functions';
import { useTheme } from '@mui/material/styles';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import { useAuth } from '../../context/AuthContext';
import PlayerDetailModal from '../../components/PlayerDetailModal';
import { page } from '../../theme/commonStyles';
import { useNotification } from '../../context/NotificationContext';

const positionOrder = { 'Goalkeeper': 1, 'Defender': 2, 'Midfielder': 3, 'Attacker': 4, 'Coach': 5 };
const translatedPositions = {
    'Goalkeeper': 'Keeper',
    'Defender': 'Verdediger',
    'Midfielder': 'Middenvelder',
    'Attacker': 'Aanvaller',
    'Coach': 'Trainer'
};

const translatedStageNames = {
    'Regular Season': 'Competitie',
    'Group Stage': 'Groepsfase',
    'League Stage': 'Poulefase',
    'Qualification Round': 'Kwalificatieronde',
    'Knockout Round Play-offs': 'Tussenronde',
    '8th Finals': 'Achtste finale',
    'Quarter-finals': 'Kwartfinale',
    'Semi-finals': 'Halve finale',
    'Final': 'Finale'
};

const getTranslatedStageName = (name) => {
    if (!name) return name;
    for (const key in translatedStageNames) {
        if (name.startsWith(key)) {
            return name.replace(key, translatedStageNames[key]);
        }
    }
    return name;
};

const ALL_COLUMNS = [
    { id: 'totalPoints', label: 'Totaal Punten' },
    { id: 'played', label: 'Wedstrijden' },
    { id: 'starting', label: 'Basisplaatsen' },
    { id: 'minutes', label: 'Minuten' },
    { id: 'goals', label: 'Goals' },
    { id: 'assists', label: 'Assists' },
    { id: 'saves', label: 'Reddingen' },
    { id: 'aerielsWon', label: 'Gewonnen kopduels' },
    { id: 'tackles', label: 'Tackels' },
    { id: 'interceptions', label: 'Intercepties' },
    { id: 'accuratePasses', label: 'Passes' },
    { id: 'yellow', label: 'Geel' },
    { id: 'red', label: 'Rood' }
];

function PlayerData() {
    const theme = useTheme();
    const notify = useNotification();
    const { auction } = useAuction();
    const [selectedSeason, setSelectedSeason] = useState('');
    const [stages, setStages] = useState([]);
    const [rounds, setRounds] = useState([]);
    const [selectedRound, setSelectedRound] = useState('');
    const [matches, setMatches] = useState([]);
    const [selectedMatch, setSelectedMatch] = useState('');
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [playerData, setPlayerData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [orderByField, setOrderByField] = useState('totalPoints');
    const [order, setOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 50;
    const { user } = useAuth();
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
    const [showFreePlayersOnly, setShowFreePlayersOnly] = useState(false);
    const [playerOwnershipMap, setPlayerOwnershipMap] = useState(new Map());

    useEffect(() => {
        if (auction?.season) {
            setSelectedSeason(auction.season);
        }
    }, [auction]);

    // Fetch rounds and stages when season changes
    useEffect(() => {
        const fetchRoundsAndStages = async () => {
            if (!selectedSeason) {
                setRounds([]);
                setStages([]);
                return;
            }
            
            try {
                // Fetch both rounds and stages in parallel
                const getRoundsForSeason = httpsCallable(functions, 'getRoundsForSeason');
                const getStagesForSeason = httpsCallable(functions, 'getStagesForSeason');
                
                const [roundsResult, stagesResult] = await Promise.all([
                    getRoundsForSeason({ seasonId: selectedSeason }),
                    getStagesForSeason({ seasonId: selectedSeason })
                ]);
                
                const roundsData = (roundsResult.data || []).sort((a, b) => (b.starting_at || '').localeCompare(a.starting_at || ''));
                const stagesData = stagesResult.data || [];
                
                setRounds(roundsData);
                setStages(stagesData);
            } catch (err) {
                console.error("Error fetching rounds/stages:", err);
            }
        };
        fetchRoundsAndStages();
        setSelectedRound(''); // Reset round selection when season changes
        setSelectedMatch(''); // Reset match selection
    }, [selectedSeason]);

    // Build combined rounds list including knockout stage legs
    const combinedRounds = useMemo(() => {
        const result = [];
        
        // Add regular rounds (from League Stage)
        rounds.forEach(round => {
            result.push({
                id: round.id,
                displayName: `${getTranslatedStageName(round.stage?.name)} - ${round.name}`,
                type: 'round',
                sortOrder: round.starting_at || ''
            });
        });
        
        // Add knockout stages with legs
        stages.forEach(stage => {
            // Skip League Stage (already covered by rounds)
            if (stage.name === 'League Stage') return;
            
            const translatedName = getTranslatedStageName(stage.name);
            
            // Add leg 1 and leg 2 as separate entries
            result.push({
                id: `${stage.id}-1`,
                displayName: `${translatedName} - Wedstrijd 1/2`,
                type: 'knockout',
                stageId: stage.id,
                leg: '1',
                sortOrder: stage.sort_order || 0
            });
            result.push({
                id: `${stage.id}-2`,
                displayName: `${translatedName} - Wedstrijd 2/2`,
                type: 'knockout',
                stageId: stage.id,
                leg: '2',
                sortOrder: stage.sort_order || 0
            });
        });
        
        return result;
    }, [rounds, stages]);

    useEffect(() => {
        const fetchData = async () => {
            if (!selectedSeason) return;
            
            setLoading(true);
            try {
                // 1. Fetch all players to get names, clubs, positions
                const playersSnapshot = await getDocs(seasonPlayersCollection(selectedSeason));
                const playersMap = playersSnapshot.docs.reduce((acc, doc) => {
                    acc[doc.id] = doc.data();
                    return acc;
                }, {});

                // Create a reverse map: numeric player ID -> Firestore document ID
                const playerIdToDocId = {};
                playersSnapshot.docs.forEach(doc => {
                    const playerData = doc.data();
                    if (playerData.id) {
                        playerIdToDocId[playerData.id] = doc.id;
                    }
                });

                // 2. Determine which rounds/stages to fetch
                let roundIdsToFetch = [];
                if (selectedRound) {
                    // Fetch only selected round (could be regular round or knockout stage-leg)
                    roundIdsToFetch = [selectedRound];
                } else {
                    // Fetch all rounds and knockout stages
                    // Get regular round IDs
                    const regularRoundIds = rounds.map(r => r.id);
                    
                    // Get knockout stage-leg IDs
                    const knockoutIds = stages
                        .filter(s => s.name !== 'League Stage')
                        .flatMap(s => [`${s.id}-1`, `${s.id}-2`]);
                    
                    roundIdsToFetch = [...regularRoundIds, ...knockoutIds];
                }
                
                const aggregatedData = {};
                const matchesSet = new Set();

                // 3. For each round/stage, fetch scores and aggregate
                const scorePromises = roundIdsToFetch.map(roundId => 
                    getDocs(collection(db, `playerScores/${selectedSeason}/rounds/${roundId}/scores`))
                );
                
                const roundsScoresSnapshots = await Promise.all(scorePromises);

                roundsScoresSnapshots.forEach(roundScoresSnapshot => {
                    roundScoresSnapshot.docs.forEach(doc => {
                        const score = doc.data();
                        const playerId = score.playerId;
                        const fixtureName = score.fixtureName || 'Onbekend';
                        
                        // Track unique matches
                        if (selectedRound) {
                            matchesSet.add(fixtureName);
                        }
                        
                        // Filter by match if selected
                        if (selectedMatch && fixtureName !== selectedMatch) {
                            return;
                        }
                        
                        // Skip players that don't exist in the players table
                        if (!playersMap[playerId]) {
                            return;
                        }
                        
                        if (!aggregatedData[playerId]) {
                            aggregatedData[playerId] = {
                                playerId,
                                id: playerIdToDocId[playerId], // Add Firestore document ID for scouting compatibility
                                name: playersMap[playerId]?.name || 'Onbekend',
                                club: playersMap[playerId]?.clubName || '',
                                position: playersMap[playerId]?.position || '',
                                photoUrl: playersMap[playerId]?.photoUrl || '',
                                clubLogo: playersMap[playerId]?.clubLogo || '',
                                totalPoints: 0,
                                goals: 0,
                                assists: 0,
                                minutes: 0,
                                yellow: 0,
                                red: 0,
                                saves: 0,
                                starting: 0,
                                played: 0,
                                cleanSheets: 0,
                                aerielsWon: 0,
                                tackles: 0,
                                interceptions: 0,
                                accuratePasses: 0
                            };
                        }

                        aggregatedData[playerId].totalPoints += (score.totalPoints || 0);

                        if (score.stats) {
                            aggregatedData[playerId].goals += (score.stats.goals || 0);
                            aggregatedData[playerId].assists += (score.stats.assists || 0);
                            aggregatedData[playerId].minutes += (score.stats.minutes || 0);
                            aggregatedData[playerId].yellow += (score.stats.yellow || 0);
                            aggregatedData[playerId].red += (score.stats.red || 0);
                            aggregatedData[playerId].saves += (score.stats.saves || 0);
                            aggregatedData[playerId].starting += (score.stats.starting || 0);
                            aggregatedData[playerId].played += (score.stats.played || 0);
                            aggregatedData[playerId].aerielsWon += (score.stats.aerielsWon || 0);
                            aggregatedData[playerId].tackles += (score.stats.tackles || 0);
                            aggregatedData[playerId].interceptions += (score.stats.interceptions || 0);
                            aggregatedData[playerId].accuratePasses += (score.stats.accuratePasses || 0);
                        }
                    });
                });

                setPlayerData(Object.values(aggregatedData));
                setMatches(Array.from(matchesSet).sort());

            } catch (err) {
                console.error("Error fetching player data:", err);
                notify.error("Kon spelersdata niet ophalen.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedSeason, selectedRound, selectedMatch, rounds, stages, notify]);

    // Fetch player ownership data
    useEffect(() => {
        const fetchOwnership = async () => {
            if (!auction?.id) return;

            try {
                const transfersQuery = query(
                    collection(db, 'auctions', auction.id, 'auctionTransfers'),
                    orderBy('timestamp', 'asc')
                );
                const transfersSnap = await getDocs(transfersQuery);

                const ownershipMap = new Map();
                transfersSnap.docs.forEach(doc => {
                    const transfer = doc.data();
                    // Track current owner for each player
                    ownershipMap.set(String(transfer.playerId), transfer.to);
                });

                setPlayerOwnershipMap(ownershipMap);
            } catch (err) {
                console.error("Error fetching ownership data:", err);
            }
        };

        fetchOwnership();
    }, [auction]);

    const handleSort = (property) => {
        const isAsc = orderByField === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderByField(property);
        setCurrentPage(1); // Reset page on sort
    };

    const sortedData = useMemo(() => {
        let data = [...playerData];
        
        // Filter by free players if checkbox is checked
        if (showFreePlayersOnly) {
            data = data.filter(player => {
                const owner = playerOwnershipMap.get(String(player.playerId));
                return !owner || owner === 'freemarket';
            });
        }
        
        return data.sort((a, b) => {
            let valueA = a[orderByField];
            let valueB = b[orderByField];

            if (orderByField === 'position') {
                valueA = positionOrder[valueA] || 99;
                valueB = positionOrder[valueB] || 99;
            } else if (typeof valueA === 'string') {
                valueA = valueA.toLowerCase();
                valueB = valueB.toLowerCase();
            }

            if (valueA < valueB) {
                return order === 'asc' ? -1 : 1;
            }
            if (valueA > valueB) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [playerData, orderByField, order, showFreePlayersOnly, playerOwnershipMap]);

    const visibleData = useMemo(() => {
        return sortedData.slice(0, currentPage * PAGE_SIZE);
    }, [sortedData, currentPage]);

    const observer = useRef();
    const lastRowRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && visibleData.length < sortedData.length) {
                setCurrentPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, visibleData.length, sortedData.length]);

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedSeason, selectedRound, selectedMatch]);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Spelersdata</Typography>
            
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                        <FormControl fullWidth sx={{ minWidth: { xs: 150, sm: 180 } }}>
                            <InputLabel>Statistieken</InputLabel>
                            <Select
                                multiple
                                value={selectedColumns}
                                onChange={(e) => setSelectedColumns(e.target.value)}
                                renderValue={(selected) => {
                                    if (selected.length === 0) return 'Alle statistieken';
                                    return selected.map(id => ALL_COLUMNS.find(c => c.id === id).label).join(', ');
                                }}
                                label="Statistieken"
                            >
                                {ALL_COLUMNS.map((column) => (
                                    <MenuItem key={column.id} value={column.id}>
                                        <Checkbox checked={selectedColumns.indexOf(column.id) > -1} />
                                        <ListItemText primary={column.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <FormControl fullWidth disabled={!selectedSeason} sx={{ minWidth: { xs: 150, sm: 180 } }}>
                            <InputLabel>Speelronde</InputLabel>
                            <Select 
                                value={selectedRound} 
                                label="Speelronde" 
                                onChange={(e) => {
                                    setSelectedRound(e.target.value);
                                    setSelectedMatch(''); // Reset match when round changes
                                }}
                            >
                                <MenuItem value=""><em>Alle speelrondes</em></MenuItem>
                                {combinedRounds.map(round => (
                                    <MenuItem key={round.id} value={round.id}>
                                        {round.displayName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {selectedRound && (
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth sx={{ minWidth: { xs: 316, sm: 180 } }}>
                                <InputLabel>Wedstrijd</InputLabel>
                                <Select 
                                    value={selectedMatch} 
                                    label="Wedstrijd" 
                                    onChange={(e) => setSelectedMatch(e.target.value)}
                                >
                                    <MenuItem value=""><em>Alle wedstrijden</em></MenuItem>
                                    {matches.map(match => (
                                        <MenuItem key={match} value={match}>{match}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showFreePlayersOnly}
                                onChange={(e) => setShowFreePlayersOnly(e.target.checked)}
                            />
                        }
                        label="Vrije spelers"
                    />
                </Box>
            </Paper>

            {loading ? (
                <Box sx={page.loadingContainer}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: { xs: '50%', sm: '40%', md: '25%' } }}>
                                    <TableSortLabel active={orderByField === 'name'} direction={orderByField === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>
                                        Speler
                                    </TableSortLabel>
                                </TableCell>
                                {(selectedColumns.length === 0 || selectedColumns.includes('totalPoints')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'totalPoints'} direction={orderByField === 'totalPoints' ? order : 'asc'} onClick={() => handleSort('totalPoints')}>
                                            Totaal Punten
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('played')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'played'} direction={orderByField === 'played' ? order : 'asc'} onClick={() => handleSort('played')}>
                                            Wedstrijden
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('starting')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'starting'} direction={orderByField === 'starting' ? order : 'asc'} onClick={() => handleSort('starting')}>
                                            Basisplaatsen
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('minutes')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'minutes'} direction={orderByField === 'minutes' ? order : 'asc'} onClick={() => handleSort('minutes')}>
                                            Minuten
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('goals')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'goals'} direction={orderByField === 'goals' ? order : 'asc'} onClick={() => handleSort('goals')}>
                                            Goals
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('assists')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'assists'} direction={orderByField === 'assists' ? order : 'asc'} onClick={() => handleSort('assists')}>
                                            Assists
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('saves')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'saves'} direction={orderByField === 'saves' ? order : 'asc'} onClick={() => handleSort('saves')}>
                                            Reddingen
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('aerielsWon')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'aerielsWon'} direction={orderByField === 'aerielsWon' ? order : 'asc'} onClick={() => handleSort('aerielsWon')}>
                                            Gewonnen kopduels
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('tackles')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'tackles'} direction={orderByField === 'tackles' ? order : 'asc'} onClick={() => handleSort('tackles')}>
                                            Tackels
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('interceptions')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'interceptions'} direction={orderByField === 'interceptions' ? order : 'asc'} onClick={() => handleSort('interceptions')}>
                                            Intercepties
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('accuratePasses')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'accuratePasses'} direction={orderByField === 'accuratePasses' ? order : 'asc'} onClick={() => handleSort('accuratePasses')}>
                                            Passes
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('yellow')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'yellow'} direction={orderByField === 'yellow' ? order : 'asc'} onClick={() => handleSort('yellow')}>
                                            Geel
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                                {(selectedColumns.length === 0 || selectedColumns.includes('red')) && (
                                    <TableCell align="right">
                                        <TableSortLabel active={orderByField === 'red'} direction={orderByField === 'red' ? order : 'asc'} onClick={() => handleSort('red')}>
                                            Rood
                                        </TableSortLabel>
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleData.map((player, index) => (
                                <TableRow 
                                    key={player.playerId} 
                                    hover
                                    ref={visibleData.length === index + 1 ? lastRowRef : null}
                                    onClick={() => {
                                        setSelectedPlayer(player);
                                        setIsPlayerModalOpen(true);
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ position: 'relative' }}>
                                                <Avatar src={player.photoUrl} alt={player.name} sx={{ width: 40, height: 40 }}>
                                                    <SportsSoccerIcon />
                                                </Avatar>
                                                {player.clubLogo && (
                                                    <Box
                                                        component="img"
                                                        src={player.clubLogo}
                                                        alt={`${player.club} logo`}
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: -4,
                                                            right: -6,
                                                            height: 20,
                                                            width: 20,
                                                            objectFit: 'contain',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2">
                                                        {player.name}
                                                    </Typography>
                                                    {player.position && (
                                                        <Box sx={{
                                                            backgroundColor: theme.positions[translatedPositions[player.position]]?.backgroundColor || 'rgba(169, 180, 200, 0.15)',
                                                            color: theme.positions[translatedPositions[player.position]]?.color || '#a9b4c8',
                                                            width: 20,
                                                            height: 20,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            borderRadius: 1,
                                                            fontWeight: 'bold',
                                                            fontSize: '0.75rem',
                                                        }}>
                                                            {(translatedPositions[player.position] || player.position).charAt(0)}
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                                                    {player.club}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    {(selectedColumns.length === 0 || selectedColumns.includes('totalPoints')) && <TableCell align="right">{player.totalPoints}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('played')) && <TableCell align="right">{player.played}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('starting')) && <TableCell align="right">{player.starting}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('minutes')) && <TableCell align="right">{player.minutes}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('goals')) && <TableCell align="right">{player.goals}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('assists')) && <TableCell align="right">{player.assists}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('saves')) && <TableCell align="right">{player.saves}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('aerielsWon')) && <TableCell align="right">{player.aerielsWon || 0}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('tackles')) && <TableCell align="right">{player.tackles || 0}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('interceptions')) && <TableCell align="right">{player.interceptions || 0}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('accuratePasses')) && <TableCell align="right">{player.accuratePasses || 0}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('yellow')) && <TableCell align="right">{player.yellow}</TableCell>}
                                    {(selectedColumns.length === 0 || selectedColumns.includes('red')) && <TableCell align="right">{player.red}</TableCell>}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <PlayerDetailModal
                open={isPlayerModalOpen}
                onClose={() => {
                    setIsPlayerModalOpen(false);
                    setSelectedPlayer(null);
                }}
                player={selectedPlayer}
                auctionId={auction?.id}
                seasonId={selectedSeason}
                userId={user?.uid}
            />
        </Box>
    );
}

export default PlayerData;
